import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Flame } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [form, setForm] = useState({ username: "", email: "", password: "", display_name: "", is_artist: !!ref, ref });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ref) setForm((f) => ({ ...f, ref, is_artist: true })); }, [ref]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to MeloCraft, ${u.display_name || u.username}`);
      nav(u.is_artist ? "/dashboard" : "/browse");
    } catch (err) {
      toast.error(errMsg(err, "Registration failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-12">
      <form onSubmit={submit} className="w-full max-w-md border border-[#2A2A2A] bg-[#121212] p-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 grid place-items-center bg-[#FF4500] text-black"><Flame className="h-5 w-5" /></div>
          <div className="font-display text-2xl font-bold tracking-tighter">JOIN MELOCRAFT</div>
        </div>
        {ref && (
          <div className="border border-[#FF4500]/40 bg-[#FF4500]/10 px-3 py-2 mb-6 font-mono text-[11px] uppercase tracking-widest text-[#FF4500]">
            Invited via {ref} — you both get 1 free month
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Username</Label>
            <Input data-testid="reg-username" required minLength={3} value={form.username}
                   onChange={(e) => setForm({ ...form, username: e.target.value })}
                   className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Display name</Label>
            <Input data-testid="reg-display" value={form.display_name}
                   onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                   className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Email</Label>
            <Input data-testid="reg-email" required type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Password (min 6)</Label>
            <Input data-testid="reg-password" required type="password" minLength={6} value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
          </div>
          <div className="flex items-center justify-between border border-[#2A2A2A] p-3">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest">I am an artist</div>
              <div className="font-mono text-[10px] text-gray-500 mt-1">$2/mo to sell (up to 150 songs)</div>
            </div>
            <Switch data-testid="reg-is-artist" checked={form.is_artist} onCheckedChange={(v) => setForm({ ...form, is_artist: v })} />
          </div>

          <Button data-testid="reg-submit" disabled={busy} type="submit"
                  className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11">
            {busy ? "Forging..." : "Ignite my account"}
          </Button>
        </div>
        <div className="mt-6 text-center font-mono text-xs text-gray-500">
          Already forged? <Link to="/login" className="text-[#FF4500] hover:brightness-125">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
